import { z } from 'zod';

import {
  AgeRange,
  Condition,
  Defects,
  PropertyType,
  RenovationPlans,
  SpecialistConstruction,
} from '@graphql/requests';

import { zod } from '@ui-furniture/lib/utils/zod-validation';

const options = {
  ageRange: [
    {
      label: 'New',
      value: AgeRange.New,
      scale: 0,
    },
    {
      label: '0+',
      value: AgeRange.From0To10,
      scale: 1,
    },
    {
      label: '10+',
      value: AgeRange.From10To50,
      scale: 2,
    },
    {
      label: '50+',
      value: AgeRange.From50To100,
      scale: 3,
    },
    {
      label: '100+',
      value: AgeRange.Over100,
      scale: 4,
    },
  ],
  propertyType: [
    {
      label: 'Flat',
      value: PropertyType.Flat,
    },
    {
      label: 'Terraced House',
      value: PropertyType.TerracedHouse,
    },
    {
      label: 'Semi Detached House',
      value: PropertyType.SemiDetachedHouse,
    },
    {
      label: 'Detached House',
      value: PropertyType.DetachedHouse,
    },
  ],
  condition: [
    {
      label: 'Awful',
      value: Condition.Awful,
      scale: 1,
    },
    {
      label: 'Poor',
      value: Condition.Poor,
      scale: 2,
    },
    {
      label: 'Good',
      value: Condition.Good,
      scale: 3,
    },
    {
      label: 'Excellent',
      value: Condition.Excellent,
      scale: 4,
    },
  ],
  defects: [
    {
      label: 'None',
      value: Defects.None,
      scale: 0,
    },
    {
      label: 'Cosmetic',
      value: Defects.Cosmetic,
      scale: 1,
    },
    {
      label: 'Substantial',
      value: Defects.Substantial,
      scale: 2,
    },
    {
      label: 'Major',
      value: Defects.Major,
      scale: 3,
    },
  ],
  renovationPlans: [
    {
      label: 'None',
      value: RenovationPlans.None,
      scale: 0,
    },
    {
      label: 'Cosmetic',
      value: RenovationPlans.Cosmetic,
      scale: 1,
    },
    {
      label: 'Structural',
      value: RenovationPlans.Structural,
      scale: 2,
    },
    {
      label: 'Demolition',
      value: RenovationPlans.Demolition,
      scale: 3,
    },
  ],
  specialistConstruction: [
    {
      label: 'Barn/church conversion',
      value: SpecialistConstruction.BarnOrChurchConversion,
    },
    {
      label: 'Steel frame',
      value: SpecialistConstruction.SteelFrame,
    },
    {
      label: 'Specialist materials',
      value: SpecialistConstruction.SpecialistMaterials,
    },
    {
      label: 'Thatched',
      value: SpecialistConstruction.ThatchedRoof,
    },
    {
      label: 'Large extension',
      value: SpecialistConstruction.LargeExtension,
    },
  ],
};

export const createRecommendationOptions = options;
export const createRecommendationSchema = z.object({
  price: zod.currency({
    invalid_type_error: 'An estimated price is required',
    validation: (schema) =>
      schema.min(10000, 'The purchase price must be at least £10,000'),
  }),
  ageRange: zod.radioFromList(options.ageRange),
  propertyType: zod.selectFromList(options.propertyType),
  bedroomCount: z.coerce.number().int().positive().min(1).max(10),
  address: zod.oneLineAddress(),
  condition: zod.radioFromList(options.condition),
  defects: zod.radioFromList(options.defects),
  renovationPlans: zod.radioFromList(options.renovationPlans),
  specialistConstruction: z.array(
    zod.selectFromList(options.specialistConstruction)
  ),
});

export type CreateSurveyRecommendationData = z.infer<
  typeof createRecommendationSchema
>;
